import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import usePrintStore from '../store/printStore';
import GetPrint from './GetPrint';
import { getShopInfoByUuid } from '../lib/supabase/api/get-shop-info';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import FullScreenLoader from '../components/ui/FullScreenLoader';

const ShopDirectGetPrint: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const { setSelectedShop } = usePrintStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [verifiedShopId, setVerifiedShopId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  useEffect(() => {
    const verifyShop = async () => {
      // Check if shopId exists in URL
      if (!shopId) {
        setErrorMessage('Shop not found. Please check the link.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      console.log(`Verifying shop UUID: ${shopId}`);
      
      try {
        // Call API to get shop info by UUID
        const shopData = await getShopInfoByUuid(shopId);

        if (!shopData) {
          console.log('Shop not found');
          setErrorMessage('Shop not found. Please check the link.');
          setIsLoading(false);
          return;
        }

        console.log(`Shop found: ${shopData.shop_name}, Online: ${shopData.is_online}`);

        // Check if shop is online
        if (!shopData.is_online) {
          setErrorMessage(`${shopData.shop_name} is currently offline. Please check back later.`);
          setIsLoading(false);
          return;
        }

        // Shop is online, preselect it
        console.log(`Shop is online, preselecting: ${shopId}`);
        setSelectedShop(shopId);
        setVerifiedShopId(shopId);
        setIsLoading(false);

      } catch (err) {
        console.error('Exception while verifying shop:', err);
        setErrorMessage('An error occurred while verifying the shop. Please try again.');
        setIsLoading(false);
      }
    };
    
    verifyShop();
  }, [shopId, setSelectedShop, navigate]);

  const handleCloseErrorDialog = () => {
    navigate('/');
  };

  // Show loading state while checking shop
  if (isLoading) {
    return <FullScreenLoader message="Verifying shop details..." />;
  }

  // Show error dialog if there's an error
  if (errorMessage) {
    return (
      <Dialog open={true} onOpenChange={handleCloseErrorDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Unable to Access Shop</DialogTitle>
            <DialogDescription>
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>
              Return to Home
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Render the normal GetPrint component with shop pre-selected
  return <GetPrint directShopId={verifiedShopId} />;
};

export default ShopDirectGetPrint;
